import React, { useState, useEffect, ChangeEvent } from "react";
import { Link } from "react-router-dom";
import Layout from "../../shared/components/Layout";
import { Table } from "@/components/Table";
import { formatDate } from "@/utils/format";
import ReactPaginate from 'react-paginate';
import clearLocalStorage from "@/utils/clearLocalStorage";

import type { Customer, PlanLabels, StatusLabels } from "@/features/facility/flatRates/types";
import type { Pagination } from "@/types";

import getCustomers from "../api/getCustomers";
import Facility from "../../shared/types/Facility";

const Customers: React.FC = () => {
  const [facility, setFacility] = useState<Facility>();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [displayedCustomers, setDisplayedCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [nameKana, setNameKana] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 0,
    limitValue: 15,
    totalCount: 0,
    totalPages: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getCustomers();
        setFacility(response.data.facility);
        setCustomers(response.data.customers);
        setFilteredCustomers(response.data.customers);

        setPagination(prev => ({
          ...prev,
          totalCount: response.data.customers.length,
          totalPages: Math.ceil(response.data.customers.length / pagination.limitValue)
        }));
        setDisplayedCustomers(response.data.customers.slice(0, pagination.limitValue));
      } catch (error) {
        console.error("Error fetching flat rate lens data:", error);
      }
    };

    fetchData();
    clearLocalStorage();
  }, [pagination.limitValue])

  const planLabels: PlanLabels = {
    both: '両眼プラン',
    right: '片眼プラン(右)',
    left: '片眼プラン(左)'
  }

  const statusLabels : StatusLabels = {
    before_confirmation: '確定前',
    before_use: '利用前',
    in_use: '利用中'
  } 

  const editButtonFlag = (customer: Customer) => {
    const status = customer?.flatRateAgreement?.requestStatus
    if(status === 'before_confirmation') {
      return '確定待ち'
    } else if(status === 'not_found' || status === 'cancel' || status === 'confirmed') {
      return (
        <>
          <Link to={`/facility/flat_rates/customers/${customer?.custCode}`} className="c-btn__04">編集</Link>
        </>
      )
    } else {
      return(<></>);
    }
  }

  const adjustmentCustomers = displayedCustomers.map((customer: Customer) => {
    return [
      {key: 'custCode', value: customer.custCode},
      {key: 'applicantNameKana', value: customer?.flatRateAgreement?.applicant?.nameKana},
      {key: 'plan', value: planLabels[customer?.flatRateAgreement?.plan]},
      {key: 'startDate', value: formatDate(customer?.flatRateAgreement?.startDate)},
      {key: 'status', value: statusLabels[customer?.flatRateAgreement?.status]},
      {key: 'edit', value: editButtonFlag(customer) }
    ]
  });

  const handlePageClick = (data: { selected: number }) => {
    const offset = data.selected * pagination.limitValue;
    setDisplayedCustomers(filteredCustomers.slice(offset, offset + pagination.limitValue));
    setPagination(prev => ({ ...prev, currentPage: data.selected }));
  };

  const handleNameKanaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setNameKana(value);
    applyFilters(selectedStatus, value);
  };

  const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const newValue: string = event.target.value;
    setSelectedStatus(newValue);
    applyFilters(newValue, nameKana);
  };

  const applyFilters = (status: string, nameKana: string) => {
    let filtered = customers;

    if (status) {
      filtered = filtered.filter(customer => customer.flatRateAgreement?.status === status);
    }

    if (nameKana) {
      filtered = filtered.filter(customer =>
        customer.flatRateAgreement?.applicant?.nameKana.includes(nameKana)
      );
    }

    setFilteredCustomers(filtered);

    setPagination(prev => ({
      ...prev,
      totalCount: filtered.length,
      totalPages: Math.ceil(filtered.length / pagination.limitValue),
      currentPage: 0
    }));

    setDisplayedCustomers(filtered.slice(0, pagination.limitValue));
  };

  const startIndex = pagination.currentPage * pagination.limitValue + 1;
  const endIndex = Math.min(startIndex + pagination.limitValue - 1, pagination.totalCount);
  
  return (
    <React.Fragment>
      <Layout>
        <main id="page-user_information" className="wrapper">
          <section className="sec-contact sec-contact--infor">
            <div className="inner">
              <div className="sec-contact__item mbpc-83">
                <h1 className="c-ttl__01 mbsp-23">お客様登録情報変更</h1>
                <form>
                  <div className="contact-search">
                    <span>契約状況</span>
                    <div className="select-custom">
                      <select value={selectedStatus} onChange={handleSelectChange}>
                        <option value="">ー</option>
                        <option value="before_confirmation">確定前</option>
                        <option value="before_use">利用前</option>
                        <option value="in_use">利用中</option>
                      </select>
                    </div>
                    <div className="search-window">
                      <span>申込者名(カナ)</span>
                      <input
                        type="text"
                        className="input"
                        value={nameKana}
                        onChange={handleNameKanaChange}
                        style={{ width: '300px' }}
                      />
                    </div>
                    <small>{`${pagination.totalCount}件中${startIndex}〜${endIndex}表示`}</small>
                  </div>
                </form>
                <form action="/">
                  <Table
                    thList={["お客様ID", "申込者(カナ)", "契約プラン", "サービス利用開始日", "契約状況", "操作"]}
                    tdList={adjustmentCustomers}
                  />
                </form>
                <ReactPaginate
                  pageCount={pagination.totalPages}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={5}
                  onPageChange={handlePageClick}
                  containerClassName='wp-pagenavi'
                  pageClassName='page smaller'
                  pageLinkClassName='page smaller'
                  activeClassName='current' 
                  previousLabel='前へ'
                  nextLabel='次へ'
                  previousClassName=''
                  nextClassName=''
                  previousLinkClassName='previouspostslink'
                  nextLinkClassName='nextpostslink'
                  disabledClassName='disabled'
                  breakLabel='...'
                  breakClassName='page smaller'
                  breakLinkClassName='page smaller'
                />
              </div>
            </div>
          </section>
        </main>
      </Layout>
    </React.Fragment>
  )
}

export default Customers;